import { type CanvasElement, type CanvasElementType } from '@blaze-runtime/engine-domain';
import {
  type ElementRedirect,
  type PredefinedRowAction,
  type RowClickAction,
  type RowClickActionDef,
  type WithRowActions,
  type WithUnifiedRowActions,
} from '@blaze-shared/elements-domain';
import { isDefined, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import {
  hasProps,
  isElementRedirect,
  isRowClickAction,
} from '../../canvas-elements/element-model/element-props-guards';
import { type ImagePropsModelBase } from '../../elements/image/image.models';

const ELEMENTS_WITH_ACTIONS: CanvasElementType[] = ['table', 'list', 'ganttChart', 'calendar'];

export const hasCrudActions = (elementType: CanvasElementType): boolean =>
  ELEMENTS_WITH_ACTIONS.includes(elementType);

export function retrieveElementPageReferences(element: CanvasElement | undefined): string[] {
  if (!element || !hasCrudActions(element.type) || !hasProps(element)) {
    return [];
  }

  const extractPageId = (action: PredefinedRowAction | undefined): string | undefined =>
    action &&
    (action.targetType === 'Page' ||
      action.targetType === 'Dialog' ||
      action.targetType === 'Drawer')
      ? action.targetId
      : undefined;
  const extractRowClickPageIds = (action: RowClickActionDef | undefined): (string | undefined)[] =>
    Object.values(action ?? {}).map((v) => (typeof v !== 'string' ? v.targetId : undefined));

  const content = element.props && 'content' in element.props ? element.props.content : {};

  const { itemAdd, itemEdit, itemDelete, itemView, itemViewDialog, rowClick } =
    content as WithRowActions & RowClickAction;
  return [
    extractPageId(itemAdd),
    extractPageId(itemEdit),
    extractPageId(itemDelete),
    extractPageId(itemView),
    extractPageId(itemViewDialog),
    ...extractRowClickPageIds(rowClick),
  ].filter(isDefined);
}

export function clearPageReferencesMutably(
  pageId: string,
  element: CanvasElement | undefined,
): void {
  if (!element || !hasCrudActions(element.type) || !hasProps(element)) {
    return;
  }

  const content = element.props && 'content' in element.props ? element.props.content : {};

  const { itemAdd, itemEdit, itemDelete, itemView, itemViewDialog, rowClick } =
    content as WithRowActions & RowClickAction;
  clearPageActionRefMutably(pageId, itemAdd);
  const clearEdit = clearPageActionRefMutably(pageId, itemEdit);
  clearPageActionRefMutably(pageId, itemDelete);
  const clearView = clearPageActionRefMutably(pageId, itemView);
  clearPageActionRefMutably(pageId, itemViewDialog);
  clearPageActionRefMutably(pageId, rowClick);

  const unifiedRowActions = content as WithUnifiedRowActions;
  clearUnifiedActionsWithPageRefMutably(unifiedRowActions, { clearEdit, clearView });

  // TODO can be extended to remove refs from: menus, custom actions, etc.
}

function clearPageActionRefMutably(
  pageId: string,
  action: ElementRedirect | RowClickAction['rowClick'] | undefined,
): boolean {
  if (!action) {
    return false;
  }
  let cleared = false;
  if (
    isElementRedirect(action) &&
    (action.targetType === 'Page' ||
      action.targetType === 'Dialog' ||
      action.targetType === 'Drawer') &&
    action.targetId === pageId
  ) {
    action.enabled = false;
    action.targetId = undefined;
    action.targetType = undefined;
    cleared = true;
  }
  if (isRowClickAction(action)) {
    const targetAction = action.targetId;
    if (typeof targetAction !== 'string' && targetAction) {
      Object.keys(targetAction).forEach((key: string) => {
        if (
          targetAction[key] &&
          typeof targetAction[key] !== 'string' &&
          targetAction[key].targetId === pageId
        ) {
          delete targetAction[key];
          if ((action.action as string) === key) {
            action.enabled = false;
            action.action = undefined;
            cleared = true;
          }
        }
      });
    }
  }
  return cleared;
}

function clearUnifiedActionsWithPageRefMutably(
  actions: WithUnifiedRowActions | undefined,
  clear: { clearEdit: boolean; clearView: boolean },
): void {
  if (!actions?.unifiedRowActions) {
    return;
  }

  actions.unifiedRowActions = actions.unifiedRowActions.filter(
    (action) =>
      (action.type !== 'edit' || !clear.clearEdit) &&
      (action.type !== 'view' || !clear.clearView) &&
      true,
  );
}

export function validateLineHeightWithFontSize(
  props: ImagePropsModelBase,
  fontSize: ValueWithUnitOfMeasure,
  lineHeightPath: string,
): Record<string, any> {
  const lineHeight = getValueByPath(props, lineHeightPath) as ValueWithUnitOfMeasure;

  const lineHeightPx = +lineHeight.replace('px', '');
  const fontSizePx = +fontSize.replace('px', '');

  if (lineHeightPx < fontSizePx) {
    return {
      [lineHeightPath]: fontSize,
    };
  }

  return {};
}

export function validateFontSizeWithLineHeight(
  props: ImagePropsModelBase,
  lineHeight: ValueWithUnitOfMeasure,
  fontSizePath: string,
): Record<string, any> {
  const fontSize = getValueByPath(props, fontSizePath) as ValueWithUnitOfMeasure;

  const lineHeightPx = +lineHeight.replace('px', '');
  const fontSizePx = +fontSize.replace('px', '');

  if (lineHeightPx < fontSizePx) {
    return {
      [fontSizePath]: lineHeight,
    };
  }

  return {};
}
